// Screen Training import guide — recorded-tutorial ZIPs from the Aidea Guide
// Builder Chrome extension, imported as an editable section of scenes
// (Templates modal "Screen Training" tab, or Library → Upload Files).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const screenTrainingGuide = {
  id: "screen-training",
  icon: "screen_share",
  keywords: [
    "screen training",
    "ekran eğitimi",
    "screen recording",
    "ekran kaydı",
    "zip",
    "import",
    "içe aktar",
    "recorder",
    "kaydedici",
    "kayıt",
    "chrome extension",
    "chrome eklentisi",
    "guide builder",
    "template",
    "şablon",
    "guide",
    "kılavuz",
    "step",
    "adım",
    "multi-input",
    "çoklu giriş",
    "click",
    "tıklama",
    "upload files",
    "dosya yükle",
    "capacitación en pantalla",
    "importar",
    "grabación",
    "extensión de chrome",
    "plantilla",
    "paso",
    "clic",
    "subir archivos",
  ],
  content: {
    en: {
      feature: "Templates",
      title: "Screen Training import",
      sections: [
        {
          id: "overview",
          title: "Recording with the Chrome extension",
          videoUrl: null,
          text:
            "The **Aidea Guide Builder** Chrome extension records a step-by-step tutorial of any website — a screenshot, a description, and optional audio or video for each step you perform.\n\n" +
            "When the recording is done, the extension exports it as a single **ZIP** file. Bring that file into Aidea Studio to turn it into an editable, interactive lesson instead of rebuilding every step by hand.",
        },
        {
          id: "import",
          title: "Importing the ZIP",
          videoUrl: null,
          text:
            "Open the **Scenes** panel and click **Create new slide** (the new-scene button under the scene list) to open the **Template Selection** window, then switch from **From Template** to the **Screen Training** tab. Drop your ZIP file on the upload area (or click it to browse), and the importer uploads its screenshots, audio, and video for you.\n\n" +
            "- Only ZIPs exported by the Aidea Guide Builder extension are supported — a ZIP without the recorder's guide file is rejected. A ZIP from a newer recorder version than Studio supports is rejected with a message asking you to update.\n" +
            "- The file can be up to 100 MB.\n" +
            "- A progress bar tracks the upload; leave the window open until it finishes.\n" +
            "- On success, Studio adds a **new section** named after the recording, placed after the scene selected in the scene list (or at the end), with **one scene per recorded step**. Each scene is named after its step's description (or **Step 1**, **Step 2**, …).",
        },
        {
          id: "edit",
          title: "Editing the imported steps",
          videoUrl: null,
          text:
            "Every imported step is a normal, fully editable scene:\n\n" +
            "- The screenshot is a regular image filling the scene — move, resize, or replace it like any other.\n" +
            "- Highlights drawn during recording come in as separate annotation shapes you can restyle, move, or delete; text notes become text boxes.\n" +
            "- The step's description is an editable text box, placed on the left or right as it was in the recording (or left out if it was hidden).\n" +
            "- The areas learners click or type into live in one interactive overlay — see **Setting up clicks and inputs** below.\n" +
            "- A step recorded as video comes in as a video element with a slow zoom.\n" +
            "- Narration recorded for the step is already in the scene's sound list.\n\n" +
            "From there, treat it like any other section: reorder scenes, add elements, or delete steps you don't need. Imported scenes don't force learners to finish them before moving on. Re-importing the same ZIP creates a new section; it doesn't update the old one.",
        },
        {
          id: "from-library",
          title: "Importing from the Library",
          videoUrl: null,
          text:
            "You can also import a recording from the **Library**: open **Upload Files** and switch to the **Screen Training** tab.\n\n" +
            "- Here you choose the position yourself with **Insert after which scene?** — pick a scene, or **At the end**.\n" +
            "- The import itself works the same way as from Template Selection.",
        },
        {
          id: "interactions",
          title: "Setting up clicks and inputs",
          videoUrl: null,
          text:
            "Select the interactive overlay (**Screen Training Multi-Input**) on an imported scene to edit what the learner has to do. On the canvas, drag an item to move it or use its corner handles to resize it.\n\n" +
            "- **Flow Items** lists the items in the order the learner completes them. Add more with **Add Click**, **Add Input**, or **Add Tag**, reorder them, or delete them.\n" +
            "- A **Click** item can go to the next item or jump to a specific scene (**On Click**).\n" +
            "- An **Input** item checks what the learner types: **Exact match** against an **Expected Value**, or **Random length** within a minimum and maximum. You can add a **Success Message** and a **Failure Message**.\n" +
            "- A **Tag** item shows text the learner typed into a tagged input earlier.\n" +
            "- Under **Behavior**: **Advance scene when all steps are done** (on by default), a pulsing highlight and a guide cursor on the active step, dimming the screen around it, and zooming in on it.",
        },
      ],
    },
    tr: {
      feature: "Şablonlar",
      title: "Ekran Eğitimi içe aktarma",
      sections: [
        {
          id: "overview",
          title: "Chrome eklentisiyle kayıt yapma",
          videoUrl: null,
          text:
            "**Aidea Guide Builder** Chrome eklentisi, herhangi bir web sitesindeki adım adım bir eğitimi kaydeder — gerçekleştirdiğiniz her adım için bir ekran görüntüsü, bir açıklama ve isteğe bağlı ses veya video.\n\n" +
            "Kayıt bittiğinde eklenti bunu tek bir **ZIP** dosyası olarak dışa aktarır. Her adımı elle yeniden oluşturmak yerine bu dosyayı Aidea Studio'ya getirerek düzenlenebilir, etkileşimli bir derse dönüştürün.",
        },
        {
          id: "import",
          title: "ZIP'i içe aktarma",
          videoUrl: null,
          text:
            "**Sahneler** panelini açın ve **Şablon Seçimi** penceresini açmak için **Yeni Sahne Oluştur**'a (sahne listesinin altındaki yeni sahne düğmesi) tıklayın, ardından **Şablondan** sekmesinden **Ekran Eğitimi** sekmesine geçin. ZIP dosyanızı yükleme alanına bırakın (veya tıklayıp seçin); içe aktarıcı ekran görüntülerini, sesleri ve videoları sizin için yükler.\n\n" +
            "- Yalnızca Aidea Guide Builder eklentisinin dışa aktardığı ZIP'ler desteklenir — kaydedicinin kılavuz dosyasını içermeyen bir ZIP reddedilir. Studio'nun desteklediğinden daha yeni bir kaydedici sürümüyle oluşturulmuş ZIP de, güncelleme yapmanızı isteyen bir mesajla reddedilir.\n" +
            "- Dosya en fazla 100 MB olabilir.\n" +
            "- Bir ilerleme çubuğu yüklemeyi izler; tamamlanana kadar pencereyi açık bırakın.\n" +
            "- Başarılı olduğunda Studio, sahne listesinde seçili sahnenin arkasına (veya en sona), kaydın adını taşıyan yeni bir **bölüm** ekler; bu bölümde kayıtlı her adım için **bir sahne** bulunur. Her sahne, adımın açıklamasıyla (veya **Step 1**, **Step 2** … şeklinde) adlandırılır.",
        },
        {
          id: "edit",
          title: "İçe aktarılan adımları düzenleme",
          videoUrl: null,
          text:
            "İçe aktarılan her adım, sıradan ve tamamen düzenlenebilir bir sahnedir:\n\n" +
            "- Ekran görüntüsü sahneyi dolduran normal bir görseldir — diğerleri gibi taşıyabilir, yeniden boyutlandırabilir veya değiştirebilirsiniz.\n" +
            "- Kayıt sırasında çizilen vurgular, yeniden biçimlendirebileceğiniz, taşıyabileceğiniz veya silebileceğiniz ayrı işaretleme şekilleri olarak gelir; metin notları metin kutusuna dönüşür.\n" +
            "- Adımın açıklaması düzenlenebilir bir metin kutusudur; kayıttaki gibi solda veya sağda yer alır (kayıtta gizlendiyse eklenmez).\n" +
            "- Öğrencinin tıklayacağı veya yazacağı alanlar tek bir etkileşimli kaplamada toplanır — aşağıdaki **Tıklama ve girişleri ayarlama** bölümüne bakın.\n" +
            "- Video olarak kaydedilen bir adım, yavaş yakınlaşan bir video öğesi olarak gelir.\n" +
            "- Adım için kaydedilen seslendirme, sahnenin ses listesinde zaten mevcuttur.\n\n" +
            "Buradan sonra bölümü diğerleri gibi ele alın: sahneleri yeniden sıralayın, öğe ekleyin veya ihtiyacınız olmayan adımları silin. İçe aktarılan sahneler, öğrenciyi bir sonrakine geçmeden önce sahneyi tamamlamaya zorlamaz. Aynı ZIP'i yeniden içe aktarmak eski bölümü güncellemez, yeni bir bölüm oluşturur.",
        },
        {
          id: "from-library",
          title: "Kütüphaneden içe aktarma",
          videoUrl: null,
          text:
            "Bir kaydı **Kütüphane**'den de içe aktarabilirsiniz: **Dosya Yükle**'yi açın ve **Ekran Eğitimi** sekmesine geçin.\n\n" +
            "- Burada konumu **Hangi sahneden sonra eklensin?** ile kendiniz seçersiniz — bir sahne veya **En sona ekle**.\n" +
            "- İçe aktarmanın kendisi Şablon Seçimi'ndeki ile aynı şekilde çalışır.",
        },
        {
          id: "interactions",
          title: "Tıklama ve girişleri ayarlama",
          videoUrl: null,
          text:
            "Öğrencinin ne yapması gerektiğini düzenlemek için içe aktarılan sahnedeki etkileşimli kaplamayı (**Ekran Eğitimi Çoklu Giriş**) seçin. Tuval üzerinde bir öğeyi taşımak için sürükleyin veya köşe tutamaçlarıyla yeniden boyutlandırın.\n\n" +
            "- **Akış Öğeleri**, öğeleri öğrencinin tamamlayacağı sırayla listeler. **Tıklama Ekle**, **Giriş Ekle** veya **Etiket Ekle** ile yenilerini ekleyin, sıralarını değiştirin veya silin.\n" +
            "- Bir **Tıklama** öğesi sonraki öğeye geçebilir veya belirli bir sahneye atlayabilir (**Tıklamada**).\n" +
            "- Bir **Giriş** öğesi öğrencinin yazdığını denetler: **Beklenen Değer** ile **Tam eşleşme** ya da en az ve en fazla karakter arasında **Rastgele (karakter sınırı)**. Bir **Başarı Mesajı** ve **Hata Mesajı** ekleyebilirsiniz.\n" +
            "- Bir **Etiket** öğesi, öğrencinin daha önce etiketli bir girişe yazdığı metni gösterir.\n" +
            "- **Davranış** altında: **Tüm adımlar bitince sahneyi ilerlet** (varsayılan olarak açık), aktif adımda yanıp sönen vurgu ve rehber imleç, aktif adım dışını karartma ve aktif adıma yakınlaşma.",
        },
      ],
    },
    es: {
      feature: "Plantillas",
      title: "Importar una formación en pantalla",
      sections: [
        {
          id: "overview",
          title: "Grabar con la extensión de Chrome",
          videoUrl: null,
          text:
            "La extensión de Chrome **Aidea Guide Builder** graba un tutorial paso a paso de cualquier sitio web: una captura de pantalla, una descripción y, opcionalmente, audio o vídeo por cada paso que realizas.\n\n" +
            "Cuando terminas la grabación, la extensión la exporta como un único archivo **ZIP**. Tráelo a Aidea Studio para convertirlo en una lección interactiva y editable, en lugar de reconstruir cada paso a mano.",
        },
        {
          id: "import",
          title: "Importar el ZIP",
          videoUrl: null,
          text:
            "Abre el panel **Escenas** y haz clic en **Crear nueva diapositiva** (el botón de nueva escena debajo de la lista de escenas) para abrir la ventana **Selección de plantilla**; después cambia de la pestaña **Desde Plantilla** a la pestaña **Formación en pantalla**. Suelta tu archivo ZIP en el área de carga (o haz clic en ella para buscarlo) y el importador subirá por ti las capturas, el audio y el vídeo.\n\n" +
            "- Solo se admiten los ZIP exportados por la extensión Aidea Guide Builder: un ZIP sin el archivo de guía del grabador se rechaza. Un ZIP de una versión del grabador más reciente de la que admite Studio se rechaza con un mensaje que te pide actualizar.\n" +
            "- El archivo puede ocupar hasta 100 MB.\n" +
            "- Una barra de progreso muestra la subida; deja la ventana abierta hasta que termine.\n" +
            "- Si todo va bien, Studio añade una **nueva sección** con el nombre de la grabación, colocada después de la escena seleccionada en la lista de escenas (o al final), con **una escena por cada paso grabado**. Cada escena recibe el nombre de la descripción de su paso (o **Step 1**, **Step 2**, …).",
        },
        {
          id: "edit",
          title: "Editar los pasos importados",
          videoUrl: null,
          text:
            "Cada paso importado es una escena normal y totalmente editable:\n\n" +
            "- La captura de pantalla es una imagen normal que ocupa toda la escena: muévela, cambia su tamaño o reemplázala como cualquier otra.\n" +
            "- Los resaltados dibujados durante la grabación llegan como formas de anotación independientes que puedes cambiar de estilo, mover o eliminar; las notas de texto se convierten en cuadros de texto.\n" +
            "- La descripción del paso es un cuadro de texto editable, situado a la izquierda o a la derecha como en la grabación (o se omite si estaba oculta).\n" +
            "- Las zonas donde el alumno hace clic o escribe están en una única capa interactiva: consulta **Configurar clics y entradas** más abajo.\n" +
            "- Un paso grabado como vídeo llega como un elemento de vídeo con un zoom lento.\n" +
            "- La narración grabada para el paso ya está en la lista de sonidos de la escena.\n\n" +
            "A partir de ahí, trátala como cualquier otra sección: reordena las escenas, añade elementos o elimina los pasos que no necesites. Las escenas importadas no obligan al alumno a completarlas antes de avanzar. Volver a importar el mismo ZIP crea una sección nueva; no actualiza la anterior.",
        },
        {
          id: "from-library",
          title: "Importar desde la Biblioteca",
          videoUrl: null,
          text:
            "También puedes importar una grabación desde la **Biblioteca**: abre **Subir Archivos** y cambia a la pestaña **Formación en pantalla**.\n\n" +
            "- Aquí eliges tú la posición con **¿Después de qué escena insertar?**: elige una escena o **Al final**.\n" +
            "- La importación en sí funciona igual que desde la ventana de selección de plantillas.",
        },
        {
          id: "interactions",
          title: "Configurar clics y entradas",
          videoUrl: null,
          text:
            "Selecciona la capa interactiva (**Multi-Entrada de Formación**) de una escena importada para editar lo que debe hacer el alumno. En el lienzo, arrastra un elemento para moverlo o usa los tiradores de sus esquinas para cambiar su tamaño.\n\n" +
            "- **Elementos del Flujo** enumera los elementos en el orden en que el alumno los completa. Añade más con **Añadir Clic**, **Añadir Entrada** o **Añadir etiqueta**, cambia su orden o elimínalos.\n" +
            "- Un elemento **Clic** puede pasar al siguiente elemento o saltar a una escena concreta (**Al hacer clic**).\n" +
            "- Un elemento **Entrada** comprueba lo que escribe el alumno: **Coincidencia exacta** con un **Valor Esperado**, o **Longitud aleatoria** entre un mínimo y un máximo. Puedes añadir un **Mensaje de Éxito** y un **Mensaje de Error**.\n" +
            "- Un elemento **Etiqueta** muestra el texto que el alumno escribió antes en una entrada etiquetada.\n" +
            "- En **Comportamiento**: **Avanzar la escena al completar todos los pasos** (activado por defecto), un resaltado pulsante y un cursor guía en el paso activo, oscurecer la pantalla a su alrededor y acercar el zoom sobre él.",
        },
      ],
    },
  },
};

export default screenTrainingGuide;
